// time-bucket.js — pick one of 8 hero buckets based on the visitor's real
// clock + the active location's sunrise/sunset. Caches per-day twilight in
// localStorage (24h), recomputes the bucket every 5 minutes. The bucket
// drives clip-resolver.js → which Pexels mp4 plays in the hero.

const CACHE_PREFIX = 'weatherfcc:v32826:twilight-';

export const BUCKETS = ['pre-dawn','sunrise','morning','midday','afternoon','golden-hour','dusk','night'];

// Hardcoded fallback when sunrise/sunset isn't available — derived from
// the hour of day at 40°N (handoff §5 ranges).
export function bucketForHour(h) {
  if (h >= 4  && h < 6)  return 'pre-dawn';
  if (h >= 6  && h < 8)  return 'sunrise';
  if (h >= 8  && h < 11) return 'morning';
  if (h >= 11 && h < 14) return 'midday';
  if (h >= 14 && h < 17) return 'afternoon';
  if (h >= 17 && h < 19) return 'golden-hour';
  if (h >= 19 && h < 21) return 'dusk';
  return 'night';
}

// Calendar key for the active forecast location, not the browser or UTC
// calendar. `formatToParts` avoids locale-dependent ordering.
export function localDateForTimeZone(nowMs, timeZone) {
  if (!timeZone) return null;
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(new Date(nowMs));
    const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
    return /^\d{4}$/.test(values.year || '')
      && /^\d{2}$/.test(values.month || '')
      && /^\d{2}$/.test(values.day || '')
      ? `${values.year}-${values.month}-${values.day}`
      : null;
  } catch (_) {
    return null;
  }
}

// Sun-anchored bucket per HANDOFF.md §5:
//   pre-dawn:    sunrise - 1h    → sunrise - 30m
//   sunrise:     sunrise ± 30m
//   morning:     sunrise + 30m   → solarNoon - 1.5h
//   midday:      solarNoon ± 1.5h
//   afternoon:   solarNoon + 1.5h→ sunset - 1h
//   golden:      sunset - 1h     → sunset
//   dusk:        sunset          → civil twilight end
//   night:       everything else
//
// Corrected 2026-08-01 (mirrored in the app's WDHeroBucket.forSun):
//   * the fixed sunset+2h dusk window still served a vivid sunset in
//     Providence 33 minutes after civil twilight had ended;
//   * real civil_twilight_end now closes dusk, with sunset+30m as the
//     missing/invalid-data fallback;
//   * afternoon ended at sunset-1.5h while golden began at sunset-1h, so the
//     30 minutes between them fell through every branch to 'night' — a night
//     clip in broad daylight.
export function bucketForSun(nowMs, sunriseMs, sunsetMs, civilTwilightEndMs = NaN) {
  if (!isFinite(sunriseMs) || !isFinite(sunsetMs)) return bucketForHour(new Date(nowMs).getHours());
  const HR = 60 * 60 * 1000;
  const solarNoon = (sunriseMs + sunsetMs) / 2;
  const fallbackDuskEnd = sunsetMs + 0.5 * HR;
  const duskEnd = isFinite(civilTwilightEndMs) && civilTwilightEndMs > sunsetMs
    ? civilTwilightEndMs
    : fallbackDuskEnd;
  const t = nowMs;

  if (t >= sunrise(-1*HR)    && t <  sunrise(-0.5*HR)) return 'pre-dawn';
  if (t >= sunrise(-0.5*HR)  && t <  sunrise(+0.5*HR)) return 'sunrise';
  if (t >= sunrise(+0.5*HR)  && t <  noon(-1.5*HR))    return 'morning';
  if (t >= noon(-1.5*HR)     && t <  noon(+1.5*HR))    return 'midday';
  if (t >= noon(+1.5*HR)     && t <  sunset(-1*HR))    return 'afternoon';
  if (t >= sunset(-1*HR)     && t <  sunset(0))        return 'golden-hour';
  if (t >= sunset(0)         && t <  duskEnd)          return 'dusk';
  return 'night';

  function sunrise(off) { return sunriseMs + off; }
  function sunset(off)  { return sunsetMs  + off; }
  function noon(off)    { return solarNoon + off; }
}

function cacheKey(lat, lon, date) {
  return `${CACHE_PREFIX}${lat.toFixed(3)}-${lon.toFixed(3)}-${date}`;
}

async function fetchSunForToday(lat, lon, date) {
  if (!Number.isFinite(lat) || !Number.isFinite(lon) || !/^\d{4}-\d{2}-\d{2}$/.test(date || '')) {
    return null;
  }
  const key = cacheKey(lat, lon, date);
  try {
    const cached = localStorage.getItem(key);
    if (cached) {
      const j = JSON.parse(cached);
      if (j && j.sunrise && j.sunset) return j;
    }
  } catch (_) {}
  const u = `/api/ext/sunrise-sunset?lat=${lat.toFixed(4)}&lon=${lon.toFixed(4)}&date=${encodeURIComponent(date)}&formatted=0`;
  try {
    const r = await fetch(u, { cache: 'force-cache' });
    if (!r.ok) return null;
    const j = await r.json();
    if (j && j.results && j.results.sunrise && j.results.sunset) {
      const out = {
        sunrise: j.results.sunrise,
        sunset: j.results.sunset,
        civilTwilightEnd: j.results.civil_twilight_end || null,
      };
      try { localStorage.setItem(key, JSON.stringify(out)); } catch (_) {}
      return out;
    }
  } catch (_) {}
  return null;
}

// Public API used by app.jsx: get the current bucket and a teardown
// to stop the 5-min recompute timer.
export function startBucketWatcher(getLocation, onBucket) {
  let timer = null;
  let cancelled = false;
  let lastBucket = null;

  const tick = async () => {
    const loc = getLocation();
    if (!loc || !Number.isFinite(loc.lat) || !Number.isFinite(loc.lon)) {
      const b = bucketForHour(new Date().getHours());
      if (b !== lastBucket) { lastBucket = b; onBucket(b); }
      return;
    }
    const today = localDateForTimeZone(Date.now(), loc.timeZone)
      || (/^\d{4}-\d{2}-\d{2}$/.test(loc.localDate || '')
        ? loc.localDate
        : new Date().toISOString().slice(0, 10));
    const sun = await fetchSunForToday(loc.lat, loc.lon, today);
    if (cancelled) return;
    let b;
    if (sun) {
      const sunriseMs = Date.parse(sun.sunrise);
      const sunsetMs  = Date.parse(sun.sunset);
      const civilTwilightEndMs = Date.parse(sun.civilTwilightEnd || '');
      b = bucketForSun(Date.now(), sunriseMs, sunsetMs, civilTwilightEndMs);
    } else {
      b = bucketForHour(new Date().getHours());
    }
    if (b !== lastBucket) { lastBucket = b; onBucket(b); }
  };

  tick();
  timer = setInterval(tick, 5 * 60 * 1000);
  return () => { cancelled = true; if (timer) clearInterval(timer); };
}

// Dev URL override: ?_dev_bucket=midday for screenshot QA.
export function devBucketOverride() {
  const p = new URLSearchParams(globalThis.location?.search || '');
  const b = p.get('_dev_bucket');
  return BUCKETS.includes(b) ? b : null;
}
